/** Helpers for pending multi-file evidence uploads. */

export function pendingEvidenceUrl(fileNames) {
  const names = normalizeNames(fileNames);
  if (!names.length) return null;
  return `pending-upload://${names.map(encodeURIComponent).join(',')}`;
}

export function fileNamesFromEvidenceUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return [];
  if (raw.startsWith('pending-upload://')) {
    const payload = raw.slice('pending-upload://'.length);
    return payload
      .split(',')
      .map((part) => {
        try {
          return decodeURIComponent(part.trim());
        } catch {
          return part.trim();
        }
      })
      .filter(Boolean);
  }
  const parts = raw.split('/');
  const name = parts[parts.length - 1];
  return name ? [name] : [];
}

/** Stable key for comparing evidence packages by file names (not URL scheme). */
export function evidenceFileKey(url) {
  return fileNamesFromEvidenceUrl(url)
    .map((name) => name.toLowerCase())
    .sort()
    .join('\u0000');
}

/**
 * Keep an existing vault URL when the selected file names are unchanged.
 * Only rewrite to pending-upload:// when files were added/removed.
 */
export function resolveEvidenceVaultUrl(existingUrl, fileNames) {
  const next = pendingEvidenceUrl(fileNames);
  const existing = String(existingUrl || '').trim() || null;
  if (!next) return null;
  if (existing && evidenceFileKey(existing) === evidenceFileKey(next)) {
    return existing;
  }
  return next;
}

export function mergeEvidenceFileNames(existing, nextFiles) {
  const names = normalizeNames(existing);
  const seen = new Set(names.map((n) => n.toLowerCase()));
  for (const file of nextFiles || []) {
    const name = String(file?.name || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
}

/** Accept attribute for the evidence file input. */
export const EVIDENCE_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const WORD_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const EXCEL_TYPES = new Set([
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

/** Keep PDF, image, Word, and Excel files (matches the evidence input accept attribute). */
export function filterEvidenceFiles(files) {
  return Array.from(files || []).filter((file) => {
    if (!file) return false;
    const type = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    if (type === 'application/pdf' || name.endsWith('.pdf')) return true;
    if (type.startsWith('image/')) return true;
    if (WORD_TYPES.has(type) || name.endsWith('.doc') || name.endsWith('.docx')) return true;
    if (EXCEL_TYPES.has(type) || name.endsWith('.xls') || name.endsWith('.xlsx')) return true;
    return false;
  });
}

function normalizeNames(fileNames) {
  if (!Array.isArray(fileNames)) return [];
  return fileNames.map((n) => String(n || '').trim()).filter(Boolean);
}
